import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ImageOverlay, MapContainer, Marker, Polyline } from 'react-leaflet'
import { notification } from 'antd'
import { CRS, LatLngTuple, Map } from 'leaflet'
import { useRecoilValue } from 'recoil'
import {
  buildingModeState,
  listLinkState,
  listPointState,
  selectedAreaState,
  selectedPoint,
  selectedPointDetail
} from 'store/buildingStore'
import { SelectedMode } from 'types/building'
import Krpano from 'utils/krpano'

import Spinner from 'components/atoms/Spinner'

import useMap from './hooks/useMap'
import useMapItem from './hooks/useMapItem'
import useRadar from './hooks/useRadar'
import { numberIcon, rotatedMarkerIcon } from './CustomMarker'

type LeafletRef = {
  createPolylineRef: any
  polylineRef: any
  initPositionRef: any
  mapContainerRef: Map | any
  povMarkerRef: any
  radarDragFlag: {
    isRadarMode: boolean
    isDragging: boolean
    rotate: any | null
    hgap: any | null
    mouseHoldTime: any | null
  }
}

type Props = {
  imagePath: string
  isLoadingMap: boolean
  isUnregisteredMap: boolean
}

export default function LeafletMap({
  imagePath,
  isLoadingMap,
  isUnregisteredMap
}: Props) {
  const { t } = useTranslation()
  const [imgBounds, setImgBounds] = useState<LatLngTuple>([0, 0])
  const [imageLoading, setImageLoading] = useState<boolean>()
  const [radarReady, setRadarReady] = useState(false)

  const isAddingPoint = useRef<boolean>(false)
  // react leaflet components ref
  const leafletRef = useRef<LeafletRef>({
    createPolylineRef: null,
    polylineRef: [],
    initPositionRef: null,
    mapContainerRef: null,
    povMarkerRef: null,
    radarDragFlag: {
      isRadarMode: false,
      isDragging: false,
      rotate: null,
      hgap: null,
      mouseHoldTime: null
    }
  })
  const buildingMode = useRecoilValue(buildingModeState)
  const selectedArea = useRecoilValue(selectedAreaState)
  const currentSelectedPoint = useRecoilValue(selectedPoint)
  const pointDetail = useRecoilValue(selectedPointDetail)
  const points = useRecoilValue(listPointState)
  const links = useRecoilValue(listLinkState)

  const {
    linkEventHandler,
    markerEventHandler,
    handleUpdatePointImageByDrag,
    isSyncChangedPoint,
    isFetchingPoint
  } = useMapItem({
    leafletRef
  })

  const { MapEventHandler, mapOnReady, handleAddPoint } = useMap({
    imgBounds,
    leafletRef
  })

  const { radarEventHandler, sync360ViewToArc } = useRadar({
    leafletRef
  })

  sync360ViewToArc(rotatedMarkerIcon)

  useEffect(() => {
    if (currentSelectedPoint && pointDetail) {
      setRadarReady(false)
      const timeout = setTimeout(() => {
        setRadarReady(true)
      }, 1000)
      return () => clearTimeout(timeout)
    }
    setRadarReady(false)
  }, [currentSelectedPoint, pointDetail])

  const displayRadar = useMemo(() => {
    if (!radarReady) return null
    // condition for display radar at selected point
    const radarPoint =
      selectedArea &&
      currentSelectedPoint &&
      currentSelectedPoint === pointDetail?.id
        ? points.find((point) => point.id === pointDetail?.id)
        : undefined

    if (radarPoint && radarPoint.image360) {
      const hlookat = (
        document.getElementById('embedpano-preview') as any
      )?.get('view.hlookat')

      const rotate =
        hlookat !== radarPoint.initView.h
          ? Krpano.NormalizeHGap(hlookat)
          : radarPoint.initView.h

      return (
        <Marker
          key={radarPoint.id}
          position={[radarPoint.y, radarPoint.x]}
          ref={(el) => {
            leafletRef.current.povMarkerRef = el
          }}
          zIndexOffset={440 + points.length}
          icon={rotatedMarkerIcon(
            leafletRef.current.radarDragFlag.rotate
              ? leafletRef.current.radarDragFlag.rotate
              : rotate + radarPoint.initView.radarHGap
          )}
          draggable={false}
          eventHandlers={radarEventHandler(buildingMode.selectedMode)}
        />
      )
    }
    return null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    radarReady,
    currentSelectedPoint,
    pointDetail,
    buildingMode.selectedMode,
    selectedArea,
    leafletRef.current.radarDragFlag
  ])

  useEffect(() => {
    imagePath && setImageLoading(true)
  }, [imagePath])

  const mapTarget = document.getElementById('map-container')
  if (mapTarget && selectedArea) {
    mapTarget.ondragover = (e) => e.preventDefault()
    mapTarget.ondrop = (e) => {
      e.preventDefault()
      if (!leafletRef.current.mapContainerRef) return
      const receivedData = e.dataTransfer?.getData('text') || ''
      if (!receivedData.startsWith('imagedrag-')) return

      const imageId = +receivedData.split('-')[1]
      const { lng, lat } =
        leafletRef.current.mapContainerRef.mouseEventToLatLng(e)
      const cord = leafletRef.current.mapContainerRef.mouseEventToLayerPoint(e)

      if (buildingMode.selectedMode !== 'point') return

      // check if drop position have point, if latlng is between point latlng +- 17 for min zoom
      const existedPoint = points.find((item) => {
        const pointCordinate =
          leafletRef.current.mapContainerRef.latLngToLayerPoint([
            item.y,
            item.x
          ])
        return (
          pointCordinate.x - 17 <= cord.x &&
          pointCordinate.x + 17 >= cord.x &&
          pointCordinate.y - 17 <= cord.y &&
          pointCordinate.y + 17 >= cord.y
        )
      })
      if (existedPoint) {
        if (!existedPoint.image360) {
          handleUpdatePointImageByDrag(existedPoint.id, imageId)
        } else {
          notification.error({
            message: t('translation.pointAlreadyHaveImage')
          })
        }
      } else {
        handleAddPoint(lng, lat, imgBounds, imageId)
      }
    }
    mapTarget.ondragenter = (e) => e.preventDefault()
    mapTarget.ondragleave = (e) => e.preventDefault()
  }

  if (isLoadingMap) return null

  return (
    <>
      {(isAddingPoint.current || imageLoading || isFetchingPoint) && (
        <Spinner className="z-[999]" />
      )}

      <MapContainer
        ref={(el) => {
          leafletRef.current.mapContainerRef = el
        }}
        center={[-99999, -99999]}
        zoom={0}
        zoomSnap={0.1}
        zoomDelta={0.5}
        id="map-container"
        className="area-map-container select-none"
        crs={CRS.Simple}
        zoomControl={false}
        attributionControl={false}
        whenReady={() =>
          mapOnReady(
            imagePath,
            setImgBounds,
            setImageLoading,
            isUnregisteredMap
          )
        }
      >
        {/* handle map event */}
        <MapEventHandler />

        {points.map((point) => (
          <Marker
            draggable={
              buildingMode.selectedMode === SelectedMode.Point &&
              !isSyncChangedPoint
            }
            bubblingMouseEvents={false}
            eventHandlers={markerEventHandler(point, imgBounds)}
            zIndexOffset={
              point.id === currentSelectedPoint
                ? 450 + points.length
                : 400 + point.order
            }
            // leaflet position is latlng with y=lat x=lng
            position={[point.y, point.x]}
            icon={numberIcon(
              point,
              selectedArea ? point.areaId === selectedArea : false,
              selectedArea ? point.id === currentSelectedPoint : false
            )}
            key={point.id}
          />
        ))}

        {links.map((link, index) => (
          <Polyline
            key={link.id}
            ref={(el) => {
              leafletRef.current.polylineRef[index] = { ref: el, id: link.id }
            }}
            // leaflet position is latlng with y=lat x=lng
            positions={[
              [link.point1.y, link.point1.x],
              [link.point2.y, link.point2.x]
            ]}
            color="#000"
            className="delete-able-link"
            weight={5}
            bubblingMouseEvents={false}
            eventHandlers={linkEventHandler(link.id, index)}
          />
        ))}

        {displayRadar}

        <ImageOverlay url={imagePath} bounds={[[0, 0], imgBounds]} />
      </MapContainer>
    </>
  )
}
